import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  accountTableFields,
  eecsGoTableFields,
} from '../../constants/accountTable';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Table, Tooltip } from 'antd';
import _ from 'lodash/fp';
import {
  translateFuelTypeClassificator,
  translateCleaningTechnologyClassificator,
  translateHydrogenTechnologyTypeClassificator,
  translateHydrogenFuelTypeClassificator,
  translateCountryClassificator,
  translateProductionTechnologyClassificator,
  translateFeedstockClassificator,
  translateBooleanClassificator,
} from '../../utils/translateHelpers';
import { TableActions } from '../';
import './AccountTable.css';
import gasType from '../../constants/classificators/gasType';
import dayjs from '../../utils/dayjs';
import { legalEntityFields } from '../../constants/legalEntity';
import { downloadFileByGetURL } from '../../utils/files';

const hasField = (field) => _.flow(_.first, _.get(field), _.isEmpty, (x) => !x);

class AccountTable extends Component {
  static propTypes = {
    dataList: PropTypes.array,
    eecsDataList: PropTypes.object,
    eecsTotal: PropTypes.number,
    t: PropTypes.func.isRequired,
    legalEntity: PropTypes.object,
    accountIds: PropTypes.array.isRequired,
    transferCertificates: PropTypes.func.isRequired,
    exportCertificates: PropTypes.func.isRequired,
    cancelCertificates: PropTypes.func.isRequired,
    isUserSeller: PropTypes.bool,
    fetchEecsAccountOverview: PropTypes.func.isRequired,
    fetchEecsAccountTotal: PropTypes.func.isRequired,
    fetchAllEecsGOsForAccount: PropTypes.func.isRequired,
    manufacture: PropTypes.bool,
    setEecsLoading: PropTypes.func.isRequired,
    eecsCode: PropTypes.string,
    fieldsIncludes: PropTypes.func,
  };

  state = {
    selectedRows: [],
    selectedEecsIDs: [],
    selectedEecs: [],
    eecsPageSize: 5,
  };

  eecsRows = new Map();

  populateRowsCache = (rows) => {
    rows.forEach((r) => {
      const id = r?.[eecsGoTableFields.ID];
      this.eecsRows.set(id, r);
    });
  };

  handleEecsCsvExport = () => {
    const { eecsCode } = this.props;
    const now = new Date();

    const pad = (n) => String(n).padStart(2, '0');

    const formatted = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;

    downloadFileByGetURL(
      '/eecsGo/csv',
      { eecsAccount: eecsCode },
      'account_overview_' + formatted + '.csv'
    );
  };

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.dataList, this.props.dataList)) {
      this.setState({
        selectedRows: [],
        selectedEecsIDs: [],
        selectedEecs: [],
      });
    }
    const { setEecsLoading } = this.props;
    if (this.props.eecsCode !== prevProps.eecsCode) {
      setEecsLoading(true);
      this.updateEecsTable(true).finally(() => {
        setEecsLoading(false);
      });
    }
  }

  renderExpirationTimeOrSummary = (value, record, t, other) => {
    if (record.summary === true)
      return (
        <>
          {record.biomethaneQuantity !== 0 && (
            <b className={'summaryText'}> {t('table.totalBiomethane')}</b>
          )}
          {record.hydrogenQuantity !== 0 && (
            <b className={'summaryText'}> {t('table.totalHydrogen')}</b>
          )}
        </>
      );
    else if (other) {
      return other(value);
    } else return value;
  };

  renderQuantityOrSummary = (value, record) => {
    if (record.summary === true)
      return (
        <>
          {record.biomethaneQuantity !== 0 && (
            <b className={'summaryAmount'}> {record.biomethaneQuantity}</b>
          )}
          {record.hydrogenQuantity !== 0 && (
            <b className={'summaryAmount'}> {record.hydrogenQuantity}</b>
          )}
        </>
      );
    return value;
  };

  showExpirationTime = () => {
    const { dataList } = this.props;
    return (
      hasField(accountTableFields.EXPIRATION_TIME)(dataList) &&
      !hasField(accountTableFields.HYDROGEN_FUEL_TYPE)(dataList) &&
      !hasField(accountTableFields.HYDROGEN_TECHNOLOGY)(dataList) &&
      !hasField(accountTableFields.FUEL_TYPE)(dataList) &&
      !hasField(accountTableFields.TECHNOLOGY)(dataList)
    );
  };

  showHydrogenType = () => {
    const { fieldsIncludes, dataList } = this.props;
    return (
      (hasField(accountTableFields.HYDROGEN_FUEL_TYPE)(dataList) &&
        !hasField(accountTableFields.HYDROGEN_TECHNOLOGY)(dataList)) ||
      (dataList.length === 0 &&
        fieldsIncludes(accountTableFields.HYDROGEN_FUEL_TYPE))
    );
  };

  showHydrogenTechnology = () => {
    const { fieldsIncludes, dataList } = this.props;
    return (
      hasField(accountTableFields.HYDROGEN_TECHNOLOGY)(dataList) ||
      (dataList.length === 0 &&
        fieldsIncludes(accountTableFields.HYDROGEN_TECHNOLOGY))
    );
  };

  showFuelType = () => {
    const { fieldsIncludes, dataList } = this.props;
    return (
      (hasField(accountTableFields.FUEL_TYPE)(dataList) &&
        !hasField(accountTableFields.TECHNOLOGY)(dataList)) ||
      (dataList.length === 0 && fieldsIncludes(accountTableFields.FUEL_TYPE))
    );
  };

  showCleaningTechnology = () => {
    const { fieldsIncludes, dataList } = this.props;
    return (
      hasField(accountTableFields.TECHNOLOGY)(dataList) ||
      (dataList.length === 0 && fieldsIncludes(accountTableFields.TECHNOLOGY))
    );
  };

  showSustainableBiofuel = () => {
    const { fieldsIncludes, dataList } = this.props;
    const dataRows = dataList.filter((row) => !row.summary);
    const allHydrogen =
      dataRows.length > 0 &&
      dataRows.every(
        (row) => row[accountTableFields.GAS_TYPE] === gasType.HYDROGEN_GAS_TYPE
      );
    if (allHydrogen) return false;
    const firstRow = dataList?.[0];
    const hasFieldInData =
      firstRow?.[accountTableFields.SUSTAINABLE_BIOFUEL] !== undefined;
    return (
      hasFieldInData ||
      (dataList.length === 0 &&
        fieldsIncludes(accountTableFields.SUSTAINABLE_BIOFUEL))
    );
  };

  renderGasType = (value) => {
    const { t } = this.props;
    return value ? t('table.gasType.' + value) : '';
  };

  renderProductionTime = (value) => {
    if (typeof value !== 'undefined' && value !== null && value !== '') {
      return dayjs(value, 'DD.MM.YYYY', true).format('MM.YYYY');
    }
  };

  getColumns = () => {
    const { t, dataList } = this.props;

    const columns = [];
    columns.push(
      {
        title: t('table.gas'),
        dataIndex: accountTableFields.GAS_TYPE,
        render: (value) => this.renderGasType(value),
      },
      {
        title: t('table.productionTime'),
        dataIndex: accountTableFields.PRODUCTION_TIME,
        render: (value) => this.renderProductionTime(value),
      },
      {
        title: t('table.transferDeadline'),
        dataIndex: accountTableFields.TRANSFER_DEADLINE,
      }
    );

    if (this.showExpirationTime()) {
      columns.push({
        title: t('table.expirationTime'),
        dataIndex: accountTableFields.EXPIRATION_TIME,
        // Total quantity summary
        render: (value, record) =>
          this.renderExpirationTimeOrSummary(value, record, t, null),
      });
    } else {
      columns.push({
        title: t('table.expirationTime'),
        dataIndex: accountTableFields.EXPIRATION_TIME,
      });
    }

    if (this.showHydrogenType()) {
      columns.push({
        title: t('table.hydrogenFuelType'),
        dataIndex: accountTableFields.HYDROGEN_FUEL_TYPE,
        render: (value, record) =>
          this.renderExpirationTimeOrSummary(
            value,
            record,
            t,
            translateHydrogenFuelTypeClassificator
          ),
      });
    } else if (hasField(accountTableFields.HYDROGEN_FUEL_TYPE)(dataList)) {
      columns.push({
        title: t('table.hydrogenFuelType'),
        dataIndex: accountTableFields.HYDROGEN_FUEL_TYPE,
        render: translateHydrogenFuelTypeClassificator,
      });
    }

    if (this.showHydrogenTechnology()) {
      columns.push({
        title: t('table.hydrogenTechnologyType'),
        dataIndex: accountTableFields.HYDROGEN_TECHNOLOGY,
        render: (value, record) =>
          this.renderExpirationTimeOrSummary(
            value,
            record,
            t,
            translateHydrogenTechnologyTypeClassificator
          ),
      });
    }

    if (this.showFuelType()) {
      columns.push({
        title: t('table.fuelType'),
        dataIndex: accountTableFields.FUEL_TYPE,
        render: (value, record) =>
          this.renderExpirationTimeOrSummary(
            value,
            record,
            t,
            translateFuelTypeClassificator
          ),
      });
    } else if (hasField(accountTableFields.FUEL_TYPE)(dataList)) {
      columns.push({
        title: t('table.fuelType'),
        dataIndex: accountTableFields.FUEL_TYPE,
        render: translateFuelTypeClassificator,
      });
    }

    if (this.showCleaningTechnology()) {
      columns.push({
        title: t('table.technology'),
        dataIndex: accountTableFields.TECHNOLOGY,
        render: (value, record) =>
          this.renderExpirationTimeOrSummary(
            value,
            record,
            t,
            translateCleaningTechnologyClassificator
          ),
      });
    }

    if (this.showSustainableBiofuel()) {
      columns.push({
        title: (
          <span>
            {t('table.sustainableBiofuel')}{' '}
            <Tooltip title={t('table.sustainableBiofuelTooltip')}>
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: accountTableFields.SUSTAINABLE_BIOFUEL,
        render: (value, record) => {
          if (record.summary) return '';
          if (record.gasType === gasType.HYDROGEN_GAS_TYPE) return '-';
          return translateBooleanClassificator(String(!!value));
        },
      });
    }

    columns.push({
      title: t('table.quantity'),
      dataIndex: accountTableFields.QUANTITY,
      // Total quantity summary
      render: (value, record) => this.renderQuantityOrSummary(value, record),
    });

    columns.push({
      title: '',
      dataIndex: accountTableFields.TRANSFER_DEADLINE_PASSED,
      render: (value) => {
        if (!value) {
          return null;
        }

        return (
          <InfoCircleOutlined
            title={t('deadlinePassed')}
            className="danger-icon"
          />
        );
      },
    });

    return columns;
  };

  getEecsColumns = () => {
    const { t } = this.props;

    const columns = [];
    columns.push(
      {
        title: t('table.gas'),
        dataIndex: eecsGoTableFields.GAS_TYPE,
        render: (value) => this.renderGasType(value),
      },
      {
        title: t('table.productionPeriod'),
        dataIndex: eecsGoTableFields.PRODUCTION_PERIOD,
      },
      {
        title: t('table.transferDeadline'),
        dataIndex: eecsGoTableFields.TRANSFER_DEADLINE,
      },
      {
        title: t('table.expirationTime'),
        dataIndex: eecsGoTableFields.EXPIRATION_TIME,
      },
      {
        title: t('table.country'),
        dataIndex: eecsGoTableFields.COUNTRY,
        render: (value) => translateCountryClassificator(value),
      },
      {
        title: t('table.productionTechnology'),
        dataIndex: eecsGoTableFields.PRODUCTION_TECHNOLOGY,
        render: (value) => {
          return (
            (value || '') +
            ' ' +
            translateProductionTechnologyClassificator(value)
          );
        },
      },
      {
        title: t('table.feedstock'),
        dataIndex: eecsGoTableFields.FEEDSTOCK,
        render: (value, record) => {
          return (
            (value || '') +
            ' ' +
            translateFeedstockClassificator(
              record.energySourceCode,
              record,
              true
            )
          );
        },
      },
      {
        title: t('table.productionSupport'),
        dataIndex: eecsGoTableFields.PRODUCTION_SUPPORT,
        render: (value) => translateBooleanClassificator(value),
        width: 30,
      },
      {
        title: t('table.investmentSupport'),
        dataIndex: eecsGoTableFields.INVESTMENT_SUPPORT,
        render: (value) => translateBooleanClassificator(value),
        width: 30,
      },
      {
        title: t('table.quantity'),
        dataIndex: eecsGoTableFields.QUANTITY,
      }
    );

    columns.push({
      title: '',
      dataIndex: accountTableFields.TRANSFER_DEADLINE_PASSED,
      render: (value) => {
        if (!value) {
          return null;
        }

        return (
          <InfoCircleOutlined
            title={t('deadlinePassed')}
            className="danger-icon"
          />
        );
      },
    });

    return columns;
  };

  handleSelectionChange = (selectedEecsIDs, selectedEecsRows, info) => {
    if (info?.type === 'all') {
      return;
    }
    this.setState({ selectedEecsIDs });
    this.populateRowsCache(selectedEecsRows);
    const selectedEecs = selectedEecsIDs
      .map((id) => this.eecsRows.get(id))
      .filter(Boolean);

    this.setState({ selectedEecs });
  };

  async updateEecsTable(total, page, size) {
    const { fetchEecsAccountOverview, fetchEecsAccountTotal, eecsCode } =
      this.props;
    const currentPage = page ?? 1;
    const pageSize = size ?? this.state.eecsPageSize;

    await fetchEecsAccountOverview({
      eecsAccount: eecsCode,
      pager: {
        page: currentPage - 1,
        size: pageSize,
      },
    });
    if (total && eecsCode && eecsCode !== '') {
      fetchEecsAccountTotal({
        eecsAccount: eecsCode,
      });
    }
  }

  onPageChange = (page, pageSize) => {
    this.setState({ eecsPageSize: pageSize });
    this.updateEecsTable(false, page, pageSize);
  };

  rowSelectionRegular = {
    onChange: (selectedRowKeys, selectedRows) => {
      this.setState({ selectedRows });
    },
    getCheckboxProps: (record) => {
      const biomethaneEecsSelected = Boolean(
        this.state.selectedEecsIDs?.length
      );
      const isHydrogen = record.gasType === gasType.HYDROGEN_GAS_TYPE;

      return {
        disabled:
          (biomethaneEecsSelected && isHydrogen) || record.summary === true,
      };
    },
  };
  rowSelectionMixed = {
    onChange: (selectedRowKeys, selectedRows) => {
      this.setState({ selectedRows });
    },
    columnTitle: (
      <div className={'ant-table-selection'}>
        <label className={'ant-checkbox-wrapper ant-checkbox-wrapper-disabled'}>
          <span className={'ant-checkbox ant-checkbox-disabled'}>
            <input
              type="checkbox"
              className={'ant-checkbox-input'}
              disabled
              value=""
            />
            <span className={'ant-checkbox-inner'} />
          </span>
        </label>
      </div>
    ),
    // Disable last table row selection
    getCheckboxProps: (record) => {
      const hydrogenSelected = this.state.selectedRows.some(
        (e) => e.gasType === gasType.HYDROGEN_GAS_TYPE
      );
      const biomethaneSelected = this.state.selectedRows.some(
        (e) => e.gasType === gasType.BIOMETHANE_GAS_TYPE
      );
      const biomethaneEecsSelected = Boolean(
        this.state.selectedEecsIDs?.length
      );

      if (hydrogenSelected) {
        return {
          disabled:
            record.gasType === gasType.BIOMETHANE_GAS_TYPE ||
            record.summary === true,
        };
      }
      if (biomethaneSelected || biomethaneEecsSelected) {
        return {
          disabled:
            record.gasType === gasType.HYDROGEN_GAS_TYPE ||
            record.summary === true,
        };
      }
      return {
        disabled: record.summary === true,
      };
    },
  };

  handleSelectAllAcrossPages = async () => {
    const items = await this.props.fetchAllEecsGOsForAccount({
      eecsAccount: this.props.eecsCode,
    });
    const selectedEecsIDs = items.map((r) => r.id ?? r.key);
    this.setState({
      selectedEecsIDs,
      selectedEecs: items,
    });
    this.populateRowsCache(items);
  };

  handleClearSelectionEecsTable = () => {
    this.setState({
      selectedEecsIDs: [],
      selectedEecs: [],
    });
  };

  render() {
    const {
      dataList,
      t,
      transferCertificates,
      exportCertificates,
      cancelCertificates,
      manufacture,
      legalEntity,
      eecsDataList,
      eecsTotal,
    } = this.props;
    const { selectedRows, selectedEecsIDs, selectedEecs } = this.state;
    const hasBiomethane = dataList.some(
      (e) => e.gasType === gasType.BIOMETHANE_GAS_TYPE
    );
    const hasHydrogen = dataList.some(
      (e) => e.gasType === gasType.HYDROGEN_GAS_TYPE
    );
    const checkAllIsDisabled = hasBiomethane && hasHydrogen;

    const isDeadlinePassed =
      (selectedRows || []).some((item) => item.transferDeadlinePassed) ||
      (selectedEecs || []).some((item) => item.transferDeadlinePassed);
    const isBiomethaneSelected =
      (Boolean(selectedRows?.length) &&
        selectedRows.every((e) => e.gasType === gasType.BIOMETHANE_GAS_TYPE)) ||
      Boolean(selectedEecsIDs?.length);
    const hasEecsCodeProducer = legalEntity?.get(
      legalEntityFields.EECS_CODE_PRODUCER
    );
    const cancellingIsDisabled = !(
      (selectedRows.length > 0) ^
      (selectedEecs.length > 0)
    );
    const hasEecsCodeTrader = legalEntity?.get(
      legalEntityFields.EECS_CODE_TRADER
    );
    const eecsContent = eecsDataList?.content;

    const rowSelectionEecs = {
      selectedRowKeys: this.state.selectedEecsIDs,
      onChange: this.handleSelectionChange,
      preserveSelectedRowKeys: true,
      getCheckboxProps: () => {
        const hydrogenSelected = selectedRows.some(
          (e) => e.gasType === gasType.HYDROGEN_GAS_TYPE
        );
        return {
          disabled: hydrogenSelected,
        };
      },

      onSelectAll: async (selected) => {
        if (selected) {
          await this.handleSelectAllAcrossPages();
        } else {
          this.handleClearSelectionEecsTable();
        }
      },
    };
    return (
      <React.Fragment>
        <h2>{t('table.estTitle')}</h2>
        <Table
          rowKey={accountTableFields.GO_SEARCH_RESPONSE_ROW_ID}
          dataSource={dataList}
          columns={this.getColumns()}
          pagination={false}
          rowSelection={
            checkAllIsDisabled
              ? this.rowSelectionMixed
              : this.rowSelectionRegular
          }
        />
        <br />
        {eecsContent?.length > 0 && (
          <>
            <div className="title-wrapper">
              <h2>{t('table.eecsTitle')}</h2>
              <Button onClick={this.handleEecsCsvExport}>
                {t('exportCsvBtn')}
              </Button>
            </div>
            <Table
              className="eecs-table"
              rowKey={eecsGoTableFields.ID}
              dataSource={eecsContent}
              columns={this.getEecsColumns()}
              pagination={{
                onChange: this.onPageChange,
                onShowSizeChange: this.onPageChange,
                current: eecsDataList?.number + 1,
                total: eecsDataList?.totalElements,
                pageSize: this.state.eecsPageSize,
                defaultCurrent: 1,
                pageSizeOptions: ['5', '10', '20'],
                showSizeChanger: true,
              }}
              rowSelection={rowSelectionEecs}
            />
            <div className="total-container">
              <span>{t('table.totalEecs')}</span>
              <span>{eecsTotal}</span>
            </div>
          </>
        )}
        {(selectedRows.length > 0 || Boolean(selectedEecsIDs?.length)) && (
          <TableActions>
            {((hasEecsCodeProducer && manufacture) ||
              (hasEecsCodeTrader && !manufacture)) && (
              <Button
                type="primary"
                disabled={isDeadlinePassed || !isBiomethaneSelected}
                title={isDeadlinePassed ? t('deadlinePassedExport') : ''}
                onClick={() => exportCertificates(selectedRows, selectedEecs)}
                style={{ marginRight: 10 }}
              >
                {t('buttonExport')}
              </Button>
            )}
            <Button
              type="primary"
              disabled={isDeadlinePassed}
              title={isDeadlinePassed ? t('deadlinePassed') : ''}
              onClick={() => transferCertificates(selectedRows, selectedEecs)}
            >
              {t('buttonTransfer')}
            </Button>
            {!manufacture && (
              <Button
                disabled={cancellingIsDisabled}
                type="primary"
                onClick={() => cancelCertificates(selectedRows, selectedEecs)}
                className="global-margin-left-10"
              >
                {t('buttonCancel')}
              </Button>
            )}
          </TableActions>
        )}
      </React.Fragment>
    );
  }
}

export default AccountTable;
